import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { BadgeCheck, Handshake, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DURATIONS = ["1", "3", "6", "12"];

export const Offers = () => {
  const [form, setForm] = useState({ first_name: "", phone: "", months: "12", notes: "" });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (form.first_name.length < 2 || form.phone.length < 6) {
      toast.error("Nome e telefono sono obbligatori");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/offers/longterm/requests`, { ...form, months: Number(form.months) });
      toast.success("Richiesta inviata!", { description: data.note });
      setForm({ first_name: "", phone: "", months: "12", notes: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="offerte" className="px-3 md:px-6 py-8 max-w-6xl mx-auto" data-testid="offers-section">
      <motion.h2
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        className="text-2xl md:text-3xl font-extrabold tracking-tight text-fr-ink"
        data-testid="offers-title"
      >
        Noleggio a lungo termine
      </motion.h2>
      <p className="text-sm text-gray-500 mt-1" data-testid="offers-subtitle">
        Da 1 a 12 mesi, come i grandi ma con il servizio di vicinato: manutenzione e assicurazione inclusi, cambio veicolo, km illimitati.{" "}
        <b className="text-fr-navy">Prezzo da gestionale</b> — nessuna carta di credito.
      </p>

      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="offers-grid">
        {DURATIONS.map((m, i) => (
          <motion.div
            key={m}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ delay: i * 0.06 }}
            className="rounded-2xl border border-gray-200 bg-white p-4 hover:shadow-card transition-shadow"
            data-testid={`offer-card-${m}m`}
          >
            <p className="text-sm font-extrabold text-fr-navy">{m} {m === "1" ? "mese" : "mesi"}</p>
            <ul className="mt-2 space-y-1 text-[11px] text-gray-500">
              <li className="flex gap-1.5"><BadgeCheck size={12} className="text-fr-red shrink-0 mt-0.5" /> Km illimitati</li>
              <li className="flex gap-1.5"><BadgeCheck size={12} className="text-fr-red shrink-0 mt-0.5" /> Manutenzione + RCA</li>
              <li className="flex gap-1.5"><BadgeCheck size={12} className="text-fr-red shrink-0 mt-0.5" /> Cambio veicolo</li>
            </ul>
            <p className="mt-3 text-[10px] font-extrabold tracking-wider text-gray-400">PREZZO: DA GESTIONALE</p>
            <button
              onClick={() => {
                setForm((f) => ({ ...f, months: m }));
                document.getElementById("lt-request")?.scrollIntoView({ behavior: "smooth", block: "center" });
              }}
              className="mt-2 w-full rounded-lg border border-fr-navy text-fr-navy text-xs font-bold py-2 hover:bg-fr-navy hover:text-white transition-colors"
              data-testid={`offer-cta-${m}m`}
            >
              Richiedi
            </button>
          </motion.div>
        ))}
      </div>

      <div id="lt-request" className="mt-6 rounded-2xl bg-white border border-gray-200 p-5" data-testid="lt-request-form">
        <p className="font-extrabold text-fr-navy text-sm">Richiesta lungo termine — richiamo entro 30 minuti</p>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-2">
          <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Nome e cognome *" className="h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm outline-none focus:border-fr-navy" data-testid="lt-input-name" />
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Telefono *" className="h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm outline-none focus:border-fr-navy" data-testid="lt-input-phone" />
          <select value={form.months} onChange={(e) => setForm({ ...form, months: e.target.value })} className="h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm" data-testid="lt-input-months">
            {DURATIONS.map((m) => <option key={m} value={m}>{m} {m === "1" ? "mese" : "mesi"}</option>)}
          </select>
          <button
            onClick={submit}
            disabled={loading}
            className="h-10 rounded-lg bg-fr-navy text-white font-bold text-sm hover:bg-fr-navydark flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="lt-submit"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Send size={14} />} Invia richiesta
          </button>
        </div>
      </div>

      <div className="mt-6 rounded-2xl bg-fr-navy p-5 flex flex-col md:flex-row md:items-center gap-3" data-testid="affiliation-banner">
        <Handshake size={26} className="text-white shrink-0" />
        <div className="flex-1">
          <p className="text-white font-extrabold" data-testid="affiliation-title">Programma affiliazione e partner</p>
          <p className="text-white/70 text-xs mt-0.5" data-testid="affiliation-subtitle">
            Hotel, B&B, taxi NCC e agenzie: offerte dedicate per i tuoi ospiti e commissioni sul calendario. Scrivici o chiama per il piano da gestionale.
          </p>
        </div>
        <a href="mailto:Fabrirentsrls@libero.it?subject=Programma%20affiliazione" className="rounded-xl bg-white text-fr-navy font-bold text-sm px-5 py-3 hover:bg-gray-100 transition-colors self-start md:self-auto" data-testid="affiliation-cta">
          Diventa partner
        </a>
      </div>
    </section>
  );
};

export default Offers;
